import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationService } from '../instrumentation/instrumentation.service';
import { InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { FileType } from '../models/file-item.model';
import { AccessTokenService } from '../services/access-token.service';
import { DeviceDetector } from '../services/device-detector.service';
import { OfficeRouteItem, OfficeRouteService } from '../services/office-route.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { PerformanceService } from '../services/performance.service';
import { SchedulerService } from '../services/scheduler.service';
import { UpdateContentService } from '../services/update-content.service';
import { UrlAuthenticatorService } from '../services/url-authenticator.service';
import { WindowService } from '../services/window.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UserContextService } from '../shared-context/user-context.service';
import { MruConfigService } from './mru-config.service';
import { MruDataService } from './mru-data.service';
import { MruPlusPlusV2Service } from './mru-plus-plus-v2.service';

@Injectable({
    providedIn: 'root'
})
export class MruDataServiceFactory {
    private readonly mruService: MruDataService;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentedHttp: InstrumentedHttp,
        instrumentationService: InstrumentationService,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        userContextService: UserContextService,
        mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        startupConfigService: ClientStartupConfigService,
        performanceService: PerformanceService,
        officeRouteService: OfficeRouteService,
        updateContentService: UpdateContentService,
        scheduler: SchedulerService,
        windowService: WindowService,
        capabilitiesService: CapabilitiesService,
        urlAuthenticatorService: UrlAuthenticatorService,
        deviceDetector: DeviceDetector
    ) {
        this.mruService = new MruPlusPlusV2Service(accessTokenService, instrumentationService, instrumentedHttp, contextService, officeHomeApiConfigService, userContextService, mruConfigService, platformLocation, globalErrorHandler, startupConfigService, performanceService, updateContentService, scheduler, windowService, capabilitiesService, urlAuthenticatorService, deviceDetector);

        // Don't warm up document cache if Edgeworth is enabled, since we aren't rendering MRU on page load
        // There are some start pages that don't have Edgeworth experience (e.g. Forms), but DocumentsComponent would have initialized them anyway
        if (!capabilitiesService.capabilities.edgeworthEnabled) {
            officeRouteService.subscribeToRouteChangeStart(event => {
                if (event.currentRoute) {
                    this.warmDocumentCache(event.currentRoute);
                }
            });
        }
    }

    public getMruService() {
        return this.mruService;
    }

    private warmDocumentCache(routeItem: OfficeRouteItem) {
        switch (routeItem.route) {
            case OfficeRoute.OfficeHome:
            case OfficeRoute.Pwa:
                this.mruService.getMruDocuments();
                break;
            case OfficeRoute.Start:
                if (routeItem.app !== FileType.SharePoint && routeItem.app !== FileType.OneDrive) {
                    this.mruService.getMruDocuments(new Set([routeItem.app]));
                }
                break;
        }
    }
}
